/**
 * 회원 기본정보 컬럼 정의
 * MEM_HAN 화면 전용 컬럼 정의
 */

import { CreateColumn } from '../../../../Services/agGrid/createColumn';
import { Chip } from '@mui/material';

/** 외부인 시 조직(본부~파트) 컬럼 공통 툴팁 */
const TOOLTIP_EXTERNAL_DEPT = '외부인은 본부·부서·팀·파트를 사용하지 않습니다.';

/** internalYn 0·'0'·미설정 → 내부인 / 1·'1' → 외부인 */
const IsInternalMemberRow = (data) => {
  const v = data?.internalYn;
  if (v === 1 || v === '1') return false;
  return true;
};

/** GridFilterButton — 회원유형 표시 라벨 */
const GetInternalYnFilterLabel = (data) => {
  if (!data) return '내부인';
  const v =
    data.internalYn ?? data.InternalYn ?? data.INTERNAL_YN ?? data.memberTypeCd;
  if (v === 1 || v === '1' || v === true) return '외부인';
  if (v === 0 || v === '0' || v === false) return '내부인';
  const s = String(v ?? '').trim();
  if (s === '외부인' || s === '외부' || s.toUpperCase() === 'E')
    return '외부인';
  if (s === '내부인' || s === '내부' || s.toUpperCase() === 'I')
    return '내부인';
  return IsInternalMemberRow(data) ? '내부인' : '외부인';
};

/** 외부인 행 권한을 데이터상 '기본'으로 고정 */
const ApplyDefaultAuthForExternalRow = (data, authCodeList) => {
  if (!data) return;
  const baseCd = (authCodeList || []).find((i) => i.name === '기본')?.value;
  data.authNm = '기본';
  if (baseCd != null) data.authCd = baseCd;
};

/** 외부인 행 안전보건조직 비움 */
const ClearPositionForExternalRow = (data) => {
  if (!data) return;
  data.position = '';
};

/** 외부인 행 본부·부서·팀·파트·부서코드 비움 */
const ClearDepartmentsForExternalRow = (data) => {
  if (!data) return;
  data.depNm1 = '';
  data.depNm2 = '';
  data.depNm3 = '';
  data.depNm4 = '';
  data.depCd = '';
};

/** 사업주(BUSINESS_OWNER) 행 — 목록 표시는 하되 엑셀/ID 변경·삭제에서 보호 */
export const IsBusinessOwnerMember = (row = {}) => {
  const cd = String(row?.authCd ?? row?.AuthCd ?? row?.AUTH_CD ?? '')
    .trim()
    .toUpperCase();
  if (cd === 'BUSINESS_OWNER') return true;
  const nm = String(row?.authNm ?? row?.AuthNm ?? row?.AUTH_NM ?? '').trim();
  return nm === '사업주';
};

/**
 * 로그인 ID 정규화
 * - 문자 포함(DH001 등): 원문 유지
 * - 숫자(전화번호)만: 숫자만·최대 11자
 */
export const NormalizeMemBasicLoginId = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/[A-Za-z]/.test(s)) return s;
  return s.replace(/\D/g, '').slice(0, 11);
};

/** 엑셀 업로드 매칭·삭제 동기화용 행 키 */
export const NormalizeMemBasicExcelRowKey = (row) => {
  if (!row) return '';
  const fromId = NormalizeMemBasicLoginId(row.id ?? row.ID ?? '');
  if (fromId) return fromId;
  return NormalizeMemBasicLoginId(row.tel ?? row.Tel ?? row.TEL ?? '');
};

/** 엑셀 업로드 그리드: 로그인 ID=전화번호, 숫자만·id·tel 동시 반영 (근로자용) */
const ApplyMemBasicExcelIdTelDigits = (params) => {
  const raw = params.newValue;
  const normalized = NormalizeMemBasicLoginId(raw);
  const digitsOnly = /[A-Za-z]/.test(String(raw ?? ''))
    ? normalized
    : String(raw ?? '')
        .replace(/[^0-9]/g, '')
        .slice(0, 11);
  params.data.id = digitsOnly;
  params.data.tel = digitsOnly;
  params.api?.refreshCells?.({
    rowNodes: [params.node],
    columns: ['id', 'tel'],
    force: true,
  });
  return true;
};

/**
 * 회원 기본정보 컬럼 정의 생성
 * @param {object} props - 컴포넌트 props
 * @returns {Array} 컬럼 정의 배열
 */
export const getColumnDefs1 = (props) => {
  return [
    // 체크박스 컬럼 (NO 컬럼)
    CreateColumn('no', 'NO', 'left', 100, false, false, false, 'checkbox', {
      checkboxProps: {
        DeleteNewRow1: props.DeleteNewRow1,
        RestoreRow1: props.RestoreRow1,
        gridRef1: props.gridRef1,
        rowData1: props.rowData1,
        selectedRowNosRef: props.selectedRowNosRef,
        lastCheckedIndexRef: props.lastCheckedIndexRef,
        handleRowSelection: props.handleRowSelection,
        enableDragSelection: true,
      },
      maxWidth: 100,
    }),
    // 숨김 컬럼
    CreateColumn(
      'depCd',
      '부서 코드',
      'left',
      100,
      true,
      false,
      true,
      'text',
      {}
    ),
    // 부서 컬럼 (편집불가, 툴팁)
    CreateColumn(
      'depNm2',
      '사업장명',
      'left',
      200,
      false,
      false,
      false,
      'text',
      {
        filterable: false,
        tooltip: (params) =>
          !IsInternalMemberRow(params.data)
            ? TOOLTIP_EXTERNAL_DEPT
            : '본부 선택 시 자동 입력됩니다.',
        cellClass: 'none-editable-cell',
      }
    ),
    // 본부 컬럼 (필수, 편집불가, 버튼 타입 — 외부인 비활성 / 값 없으면 '-')
    CreateColumn(
      'depNm1',
      '회사명',
      'left',
      200,
      true,
      true,
      false,
      'requiredButton',
      {
        tooltipValueGetter: (params) =>
          !IsInternalMemberRow(params.data) ? TOOLTIP_EXTERNAL_DEPT : '',
        cellClassRules: {
          'required-cell': (params) => {
            if (!IsInternalMemberRow(params.data)) return false;
            const value = params.data?.depNm1;
            if (typeof value === 'string') return !value || value.trim() === '';
            return !value;
          },
          'none-editable-cell': (params) => !IsInternalMemberRow(params.data),
        },
        buttonRenderer: (params) => {
          const value = String(params.data?.depNm1 ?? '').trim();
          const label = value ? value : '-';
          if (!IsInternalMemberRow(params.data)) {
            return (
              <span style={{ color: '#9e9e9e', fontSize: '0.9rem' }}>{''}</span>
            );
          }
          return (
            <button
              type="button"
              onClick={() => {
                props.SearchLogic2();
                props.setClickRowId(params);
                props.setModalToggle1(true);
              }}
              style={{
                color: 'green',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
              }}
            >
              {label}
            </button>
          );
        },
        buttonEmptyTooltip: '본부 설정은 필수입니다.',
        buttonValueTooltip: '재설정 시 클릭하세요.',
      }
    ),

    // 팀 컬럼 (편집불가, 툴팁)
    CreateColumn('depNm3', '부서명', 'left', 200, false, false, false, 'text', {
      filterable: false,
      tooltip: (params) =>
        !IsInternalMemberRow(params.data)
          ? TOOLTIP_EXTERNAL_DEPT
          : '본부 선택 시 자동 입력됩니다.',
      cellClass: 'none-editable-cell',
    }),
    // 파트 컬럼 (편집불가, 툴팁)
    CreateColumn('depNm4', '파트', 'left', 200, false, false, false, 'text', {
      filterable: false,
      tooltip: (params) =>
        !IsInternalMemberRow(params.data)
          ? TOOLTIP_EXTERNAL_DEPT
          : '본부 선택 시 자동 입력됩니다.',
      cellClass: 'none-editable-cell',
    }),

    // 직급 컬럼
    CreateColumn('rank', '직위', 'left', 150, true, false, false, 'text', {
      flex: 1,
      valueGetter: (params) => {
        return params.data?.rank || '';
      },
      valueSetter: (params) => {
        params.data.rank = params.newValue;
        return true;
      },
    }),
    // 성명 컬럼 (필수)
    CreateColumn('name', '성명', 'left', 180, true, true, false, 'text', {
      maxLength: 20,
      cellEditorParams: {
        parseValue: (value) => {
          return String(value);
        },
      },
      valueFormatter: (params) => {
        const val = params.value;
        return val !== undefined ? String(val) : '';
      },
    }),
    // 전화번호 (로그인 ID) 컬럼 (신규 행만 입력 · 사업주 ID 보호)
    CreateColumn(
      'id',
      '전화번호 (로그인 ID)',
      'left',
      180,
      true,
      true,
      false,
      'text',
      {
        tooltip: '신규 행에서 전화번호(로그인 ID)를 입력하세요.',
        editableCondition: (params) =>
          params.data?.newYn === 'Y' && !IsBusinessOwnerMember(params.data),
        maxLength: 20,
        cellEditorParams: {
          maxLength: 20,
          parseValue: (value) => NormalizeMemBasicLoginId(value),
        },
        valueParser: (params) => {
          if (params.newValue == null || params.newValue === '') return '';
          return NormalizeMemBasicLoginId(params.newValue);
        },
        valueFormatter: (params) => {
          const v = params.value;
          if (v == null || v === '') return '';
          return NormalizeMemBasicLoginId(v);
        },
        valueSetter: (params) => {
          if (IsBusinessOwnerMember(params.data)) return false;
          const normalized = NormalizeMemBasicLoginId(params.newValue);
          params.data.id = normalized;
          if (!/[A-Za-z]/.test(normalized)) {
            params.data.tel = normalized;
          }
          params.api?.refreshCells?.({
            rowNodes: [params.node],
            columns: ['tel'],
            force: true,
          });
          return true;
        },
        cellClassRules: {
          'required-cell': (params) => {
            if (params.data?.newYn !== 'Y') return false;
            if (IsBusinessOwnerMember(params.data)) return false;
            const value = params.value;
            if (typeof value === 'string') return value.trim() === '';
            return !value;
          },
          'none-editable-cell': (params) =>
            params.data?.newYn !== 'Y' || IsBusinessOwnerMember(params.data),
        },
      }
    ),
    // 회원유형 — internalYn: 0 내부인, 1 외부인
    CreateColumn(
      'internalYn',
      '회원유형',
      'center',
      130,
      true,
      false,
      false,
      'text',
      {
        filterable: true,
        filterOptions: ['내부인', '외부인'],
        filterValueGetter: (params) => GetInternalYnFilterLabel(params?.data),
        minWidth: 120,
        maxWidth: 140,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['내부인', '외부인'],
        },
        valueGetter: (params) => {
          const v = params.data?.internalYn;
          const isExt = v === 1 || v === '1';
          return isExt ? '외부인' : '내부인';
        },
        valueSetter: (params) => {
          params.data.internalYn = params.newValue === '외부인' ? '1' : '0';
          if (params.newValue === '외부인') {
            ApplyDefaultAuthForExternalRow(params.data, props.authCodeList);
            ClearPositionForExternalRow(params.data);
            ClearDepartmentsForExternalRow(params.data);
          }
          params.api?.refreshCells?.({
            rowNodes: [params.node],
            columns: [
              'address',
              'bloodType',
              'affiliationNm',
              'depNm1',
              'depNm2',
              'depNm3',
              'depNm4',
              'authNm',
              'position',
            ],
            force: true,
          });
          return true;
        },
        cellRenderer: (params) => {
          const v = params.data?.internalYn;
          const isExt = v === 1 || v === '1';
          const label = isExt ? '외부인' : '내부인';
          const bg = isExt ? '#ed6c02' : '#1976d2';
          return (
            <span
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Chip
                size="small"
                label={label}
                sx={{
                  bgcolor: bg,
                  color: '#fff',
                  fontWeight: 700,
                  height: 22,
                }}
              />
            </span>
          );
        },
      }
    ),

    // 생년월일 컬럼 (필수)
    CreateColumn('birth', '생년월일', 'left', 100, true, true, false, 'date', {
      valueFormatter: (params) => {
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${year}-${month}-${day}`;
      },
      valueParser: (params) => {
        const [year, month, day] = params.newValue.split('-');
        const parsedDate = new Date(
          Date.UTC(year, month - 1, day)
        ).toISOString();

        if (parsedDate.substr(0, 10) === params.data?.birth?.substr(0, 10)) {
          return params.data.birth;
        }

        return parsedDate;
      },
      cellClassRules: {
        'required-cell': (params) =>
          !params.value || params.value.trim() === '',
      },
    }),
    // 혈액형 컬럼 (외부인일 때 비활성)
    CreateColumn(
      'bloodType',
      '혈액형',
      'left',
      120,
      true,
      false,
      false,
      'text',
      {
        flex: 1,
        editableCondition: (params) => IsInternalMemberRow(params.data),
        cellClassRules: {
          'none-editable-cell': (params) => !IsInternalMemberRow(params.data),
        },
        tooltipValueGetter: (params) =>
          !IsInternalMemberRow(params.data)
            ? '외부인은 혈액형을 입력하지 않습니다.'
            : '',
        valueGetter: (params) => {
          return params.data.bloodType || '';
        },
        valueSetter: (params) => {
          params.data.bloodType = params.newValue;
          return true;
        },
      }
    ),
    // 주소 컬럼 (외부인일 때 비활성)
    CreateColumn('address', '주소', 'left', 120, true, false, false, 'text', {
      filterable: false,
      flex: 1,
      editableCondition: (params) => IsInternalMemberRow(params.data),
      cellClassRules: {
        'none-editable-cell': (params) => !IsInternalMemberRow(params.data),
      },
      tooltipValueGetter: (params) =>
        !IsInternalMemberRow(params.data)
          ? '외부인은 주소를 입력하지 않습니다.'
          : '',
      valueGetter: (params) => {
        return params.data.address || '';
      },
      valueSetter: (params) => {
        params.data.address = params.newValue;
        return true;
      },
    }),
    // 국적 컬럼
    CreateColumn('nation', '국적', 'left', 120, true, false, false, 'text', {
      flex: 1,
      valueGetter: (params) => {
        return params.data.nation || params.data.NATION || '대한민국';
      },
      valueSetter: (params) => {
        params.data.nation = params.newValue;
        params.data.NATION = params.newValue;
        return true;
      },
    }),
    // 활성화 여부 컬럼 (편집불가)
    CreateColumn(
      'useYn',
      '활성화 여부',
      'center',
      120,
      false,
      false,
      false,
      'text',
      {
        flex: 1,
        headerComponentParams: {
          template:
            '<div class="ag-header-cell-label"><span>활성화<br>여부</span></div>',
        },
        valueGetter: (params) => {
          return params.data.useYn || 'Y';
        },
        valueSetter: (params) => {
          params.data.useYn = params.newValue;
          return true;
        },
      }
    ),
    // 권한 컬럼 (셀렉트박스)
    CreateColumn(
      'authNm',
      '권한',
      'center',
      120,
      true,
      false,
      false,
      'select',
      {
        selectOptions: props.authCodeList
          .filter((e) => e.value !== 'BUSINESS_OWNER')
          .map((item) => item.name),
        defaultValue: '기본',
        selectValueKey: 'value',
        selectLabelKey: 'name',
        cellClass: (params) => {
          if (
            params.data?.authCd === 'BUSINESS_OWNER' ||
            !IsInternalMemberRow(params.data)
          )
            return 'none-editable-cell';
          return '';
        },
        editableCondition: (params) => {
          return (
            params.data?.authCd !== 'BUSINESS_OWNER' &&
            IsInternalMemberRow(params.data)
          );
        },
        tooltipValueGetter: (params) =>
          !IsInternalMemberRow(params.data)
            ? '외부인은 권한이 기본으로 고정됩니다.'
            : '',
        valueGetter: (params) => {
          if (!IsInternalMemberRow(params.data)) return '기본';
          return params.data.authNm || '기본';
        },
        valueSetter: (params) => {
          if (!IsInternalMemberRow(params.data)) {
            ApplyDefaultAuthForExternalRow(params.data, props.authCodeList);
            return true;
          }
          const newAuthNm = params.newValue;
          const newAuthCd = props.authCodeList.find(
            (item) => item.name === newAuthNm
          )?.value;

          params.data.authNm = newAuthNm;
          params.data.authCd = newAuthCd;
          return true;
        },
        flex: 1,
      }
    ),
  ];
};

/**
 * 부서정보 컬럼 정의 생성
 * @returns {Array} 컬럼 정의 배열
 */
export const getColumnDefs2 = () => {
  return [
    CreateColumn(
      'depCd',
      '부서코드',
      'left',
      100,
      false,
      false,
      true,
      'text',
      {}
    ),
    CreateColumn('depNm1', '회사명', 'left', 120, false, false, false, 'text', {
      flex: 1,
    }),
    CreateColumn('depNm2', '사업장명', 'left', 120, false, false, false, 'text', {
      flex: 1,
    }),
    CreateColumn('depNm3', '부서명', 'left', 120, false, false, false, 'text', {
      flex: 1,
    }),
    CreateColumn('depNm4', '파트', 'left', 120, false, false, false, 'text', {
      flex: 1,
    }),
  ];
};

/**
 * 엑셀 업로드 컬럼 정의 생성
 * @returns {Array} 컬럼 정의 배열
 */
export const getColumnDefs3 = () => {
  return [
    // 체크박스 컬럼 (NO 컬럼)
    CreateColumn('no', 'NO', 'left', 100, false, false, false, 'checkbox', {}),
    // 숨김 컬럼
    CreateColumn(
      'depCd',
      '부서 코드',
      'left',
      100,
      true,
      false,
      true,
      'text',
      {}
    ),
    // 회사명 컬럼 (필수)
    CreateColumn(
      'depNm1',
      '* 회사명 (필수)',
      'left',
      120,
      true,
      true,
      false,
      'text',
      {}
    ),
    // 사업장명 컬럼
    CreateColumn(
      'depNm2',
      '사업장명',
      'left',
      100,
      false,
      false,
      false,
      'text',
      {}
    ),
    // 부서명 컬럼
    CreateColumn('depNm3', '부서명', 'left', 100, false, false, false, 'text', {}),
    // 파트 컬럼
    CreateColumn(
      'depNm4',
      '파트',
      'left',
      100,
      false,
      false,
      false,
      'text',
      {}
    ),
    // 권한 컬럼
    CreateColumn(
      'authNm',
      `권한 (미입력시 '기본' 기입)`,
      'left',
      100,
      true,
      false,
      false,
      'text',
      {}
    ),

    // 직위 컬럼
    CreateColumn('rank', '직위', 'left', 100, true, false, false, 'text', {}),
    // 성명 컬럼 (필수)
    CreateColumn(
      'name',
      '* 성명 (필수)',
      'left',
      100,
      true,
      true,
      false,
      'text',
      {}
    ),
    // 전화번호 (로그인 ID) 컬럼 (신규 행 식별용 — 엑셀에서 입력)
    CreateColumn(
      'id',
      '* 전화번호 (로그인 ID)',
      'left',
      140,
      true,
      true,
      false,
      'text',
      {
        tooltip: '로그인 ID로 사용할 전화번호',
        maxLength: 11,
        cellEditorParams: {
          maxLength: 11,
          parseValue: (value) =>
            String(value ?? '')
              .replace(/[^0-9]/g, '')
              .slice(0, 11),
        },
        valueParser: (params) => {
          if (params.newValue == null || params.newValue === '') return '';
          return String(params.newValue)
            .replace(/[^0-9]/g, '')
            .slice(0, 11);
        },
        valueFormatter: (params) => {
          const v = params.value;
          if (v == null || v === '') return '';
          return NormalizeMemBasicLoginId(v);
        },
        valueSetter: (params) => ApplyMemBasicExcelIdTelDigits(params),
      }
    ),
    CreateColumn(
      'internalYn',
      '회원유형 (0=내부인, 1=외부인)',
      'left',
      160,
      true,
      false,
      false,
      'text',
      {}
    ),
    CreateColumn(
      'affiliationNm',
      '업체명',
      'left',
      140,
      true,
      false,
      false,
      'text',
      {}
    ),
    // 생년월일 컬럼
    CreateColumn(
      'birth',
      '생년월일',
      'left',
      100,
      true,
      false,
      false,
      'date',
      {}
    ),
    // 혈액형 컬럼
    CreateColumn(
      'bloodType',
      '혈액형',
      'left',
      100,
      true,
      false,
      false,
      'text',
      {}
    ),
    // 주소 컬럼
    CreateColumn(
      'address',
      '주소',
      'left',
      100,
      true,
      false,
      false,
      'text',
      {}
    ),
    // 국적 컬럼
    CreateColumn('nation', '국적', 'left', 100, true, false, false, 'text', {}),
  ];
};

const columnDefs = {
  getColumnDefs1,
  getColumnDefs2,
  getColumnDefs3,
};

export default columnDefs;
